import { openMailto } from "@/utils/mailto";
import Hero from "../components/hero";
import heroImage from "@assets/images/tarrif-hero.svg";
import {
  billBreakdown,
  correctionFactorsData,
  securityDeposits,
  standardMonthlyFee,
  tarrifs,
} from "../data";
import Help from "../components/help";
import Table from "../components/table";

const heroData = {
  title: "Transparent Tariffs, Certified Fair Pricing, and No Hidden Fees.",
  description:
    "At Gas Integrated Solutions L.L.C. (GASIS), we believe in full transparency. Our tariffs reflect international LPG market benchmarks, UAE regulatory standards and certified safety and service quality.",
  image: heroImage,
  buttonText: "View Residential Tariffs",
  buttonOnclick: () => window.location.href = "/business/services/tariffs",
  footerText:
    "We provides safe, certified, and efficient gas connections across the UAE delivered and activated quickly by our licensed technicians",
  footerButtonText: "Talk to Our Team",
  footerButtonOnclick: () => openMailto({ subject: "Tariffs & Charges Inquiry" }),
};

const Tarrifs = () => {
  return (
    <div className="bg-white">
      <Hero data={heroData} />

      {/* COMMERCIAL & RETAIL SECTION */}
      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-gis-dark-blue text-center">
            Commercial & Retail Tariffs
          </h2>
          <p className="mt-4 text-[#62636C] text-sm md:text-base lg:text-xl text-center max-w-[900px] mx-auto">
            Competitive rates for businesses of all sizes, from small cafés to
            large industrial facilities.
          </p>

          <div className="grid gap-4 mt-8 md:mt-16 md:grid-cols-2 xl:grid-cols-3 lg:gap-6">
            {tarrifs.map((data, index) => (
              <article
                key={index}
                className="p-3 md:p-6 bg-white rounded-[12px] md:rounded-[24px]"
              >
                <span className="text-[14px] md:text-xs lg:text-sm text-tomato-200 font-medium">
                  {data.title}
                </span>
                <h3 className="text-[#090D2C] text-sm md:text-lg lg:text-xl mt-3 md:mt-6 font-semibold leading-[1.1]">
                  {data.price}{" "}
                  <span className="text-[#0A0E2EBF] text-xs md:text-base font-normal">
                    Monthly
                  </span>
                </h3>

                <p className="mt-4 text-[14px] md:text-xs md:mt-6 lg:text-sm">
                  {data.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STANDARD MONTHLY FEE */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[54px] font-semibold text-center">
            Standard Monthly & One-Time Fees
          </h2>

          <div className="mt-10 lg:mt-[90px] space-y-4">
            {standardMonthlyFee.map((data, index) => (
              <article
                key={index}
                className="p-1 sm:p-4 border-b border-[#62636C33]"
              >
                <div className="flex items-start justify-between gap-2 sm:items-center">
                  <h3 className="text-xs md:text-base lg:text-xl text-[#090D2C] font-semibold leading-snug">
                    {data.title}
                  </h3>
                  <span
                    style={{
                      backgroundColor: data.bgColor,
                      color: data.textColor,
                    }}
                    className="py-1 sm:py-2 px-2 sm:px-3 text-[10px] text-nowrap md:text-[14px] lg:text-xs rounded-md border border-[#62636C33]"
                  >
                    Charged: <strong>{data.charged}</strong>
                  </span>
                </div>
                <p className="mt-1 sm:mt-2 text-[14px] md:text-xs lg:text-base text-[#0A0E2EBF]">
                  {data.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="flex flex-col gap-10 xl:gap-20 lg:items-center lg:flex-row">
            <div className="">
              <h2 className="text-[24px] md:text-[40px] lg:text-[54px] font-semibold text-gis-dark-blue">
                Security Deposit Details
              </h2>
              <p className="mt-4 text-[#62636C] text-xs md:text-base lg:text-lg">
                Terms:
              </p>
              <div className="mt-2 space-y-2">
                {[
                  "Held against unpaid invoices, damages, or liabilities.",
                  "Fully refundable within 7–10 working days after account closure and final bill settlement.",
                  "May be transferred to another GASIS property if account continues.",
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 size-2 md:size-3 bg-[#62636C] rounded-full mt-2 md:mt-3" />
                    <span className="text-[#62636C] text-xs md:text-base lg:text-lg">
                      {feature}
                    </span>
                  </li>
                ))}
              </div>
            </div>

            <div className="w-full lg:min-w-[450px] xl:min-w-[600px] xl:w-[600px] bg-white border border-[#62636C33] shadow-lg shadow-gray-300 rounded-md md:rounded-lg overflow-clip">
              <span className="inline-block w-full px-4 py-3 text-xs text-white md:text-base lg:text-lg bg-tomato-200">
                Deposits per Unit Type
              </span>
              <div>
                {securityDeposits.map((data, index) => (
                  <article
                    key={index}
                    className="flex justify-between text-[#62636C] text-xs md:text-base"
                  >
                    <span className="px-4 py-2 border-r border-[#62636C] flex-1">
                      {data.title}
                    </span>
                    <span className="px-4 py-2 w-[100px] sm:w-[120px] inline-block">
                      {data.price}{" "}
                      <span className="text-[12px] md:text-xs">AED</span>
                    </span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[54px] font-semibold text-gis-dark-blue">
            Correction Factors (Technical Reference)
          </h2>

          <Table className="mt-5 md:mt-10" data={correctionFactorsData} />

          <p className="mt-4 md:mt-8 text-[#1E1F24] text-xs md:text-base lg:text-xl px-4 md:px-10 py-3 md:py-6 bg-[#F1F1F1] rounded-md md:rounded-lg">
            <strong className="text-[#090D2C]">Total Consumption (m³) </strong>{" "}
            = Meter Reading × Correction Factor
          </p>

          <span className="my-12 border-t border-[#B9BBC6] block" />

          <h2 className="text-[24px] md:text-[40px] lg:text-[54px] font-semibold text-gis-dark-blue">
            Example: Sample Residential Bill Breakdown
          </h2>

          <Table
            className="mt-5 md:mt-10"
            data={billBreakdown}
            columnSpans={{ Component: 2 }}
          />
          <div className="flex bg-[#FCFCFC] border-t-transparent items-center border md:border-2 border-[#62636C]/40 divide-x md:divide-x-2 divide-[#62636C]/40 text-[#090D2C] text-[14px] md:text-lg lg:text-xl font-semibold">
            <span className="px-2 py-3 sm:px-4 flex-[3]">Total Payable</span>
            <span className="flex-1 px-2 py-3 sm:px-4">414.75 AED</span>
          </div>

          <p className="mt-4 md:mt-8 text-[#1E1F24] text-xs md:text-base lg:text-xl px-4 md:px-10 py-3 md:py-6 bg-[#F1F1F1] rounded-md md:rounded-lg">
            <strong className="text-[#090D2C]">Tip: </strong>
            <br />
            Submit your meter reading monthly to ensure accurate billing and
            avoid estimates.
          </p>
        </div>
      </section>

      {/* MINIMUM MONTHLY CHARGE */}
      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-[24px] md:text-[40px] lg:text-[54px] max-w-[970px] mx-auto font-semibold text-gis-dark-blue text-center">
            Minimum Monthly Charge/Additional Fees and Conditions
          </h2>

          <div className="flex flex-col gap-6 mt-6 md:mt-12 xl:flex-row">
            <div className="flex-1 p-3 bg-white rounded-md md:rounded-lg md:p-6 lg:p-10">
              <h3 className="text-[#090D2C] font-semibold text-base md:text-xl lg:text-[40px]">
                Minimum Monthly Charge (MMC)
              </h3>
              <p className="mt-4 text-[#1E1F24] text-xs md:text-base lg:text-lg">
                A Minimum Monthly Charge may apply to each connected unit
                regardless of consumption. The MMC ensures network readiness,
                safety, and administrative costs are covered — particularly for
                vacant or low-use units. MMC values may vary by property type
                and are agreed under the service agreement with the building
                owner or management.
              </p>
            </div>

            <div className="flex-1 p-3 bg-white rounded-md md:rounded-lg md:p-6 lg:p-10">
              <h3 className="text-[#090D2C] font-semibold text-base md:text-xl lg:text-[40px]">
                Additional Fees and Conditions
              </h3>

              <div className="mt-4 space-y-2">
                {[
                  "Returned payments and bank fees are charged at actual cost.",
                  "Extraordinary services, emergency works, or non-routine interventions outside the standard scope are charged separately under Annex E – Special Works.",
                  "All customers are deemed to have accepted these charges by virtue of receiving GASIS supply or services.",
                  "Fees and tariffs may be revised annually or under extraordinary circumstances, in line with UAE regulations and LPG market adjustments.",
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 size-2 md:size-3 bg-[#1E1F24] rounded-full mt-2 md:mt-3" />
                    <span className="text-[#1E1F24] text-xs md:text-base lg:text-lg">
                      {feature}
                    </span>
                  </li>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 lg:py-[110px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <div className="flex flex-col gap-3 lg:gap-10 lg:flex-row">
            <div className="max-w-[600px] xl:max-w-[788px]">
              <h2 className="text-gis-dark-blue text-[26px] md:text-[40px] lg:text-[60px] font-semibold">
                Taxes & Compliance
              </h2>
              <p className="mt-4 text-[#62636C] text-xs md:text-base lg:text-lg">
                All fees and tariffs are exclusive of VAT (5%).
                <br />
                GASIS is fully registered with the Federal Tax Authority (FTA)
                and issues tax invoices showing its TRN number on every bill.{" "}
                <br />
                Regulatory compliance is maintained under:
              </p>
            </div>

            <div className="mt-4 space-y-2">
              {[
                "UAE Fire & Safety Code of Practice (2021)",
                "Dubai Civil Defense LPG Guidelines",
                "ESMA Gas Safety Standards",
                "FTA VAT Regulations",
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 size-2 md:size-3 bg-[#62636C] rounded-full mt-2 md:mt-3" />
                  <span className="text-[#62636C] text-xs md:text-base lg:text-lg">
                    {feature}
                  </span>
                </li>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HELP SECTION */}
      <Help />
    </div>
  );
};

export default Tarrifs;
